// src/components/MarkdownRenderer.js
import React from 'react';
// Temporarily disable markdown imports to fix build
// import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
// import rehypeSanitize from 'rehype-sanitize';

/**
 * MarkdownRenderer Component
 * Safely renders markdown content with security sanitization and custom styling
 * 
 * Features:
 * - GitHub Flavored Markdown support (tables, strikethrough, etc.)
 * - Security sanitization to prevent XSS
 * - Custom styling to match Tailwind design system
 * - Clickable links with proper security attributes
 * - Preserved line breaks and formatting
 */

/**
 * Custom components for styling markdown elements
 */
const markdownComponents = {
  // Headings
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold text-gray-900 mb-3 mt-5 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4 first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold text-gray-900 mb-2 mt-3 first:mt-0">
      {children}
    </h4>
  ),
  h5: ({ children }) => (
    <h5 className="text-sm font-semibold text-gray-900 mb-1 mt-2 first:mt-0">
      {children}
    </h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-xs font-semibold text-gray-900 mb-1 mt-2 first:mt-0">
      {children}
    </h6>
  ),

  // Paragraphs
  p: ({ children }) => (
    <p className="text-gray-600 leading-relaxed mb-3 last:mb-0">
      {children}
    </p>
  ),

  // Links
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 underline hover:no-underline transition-colors duration-200"
    >
      {children}
    </a>
  ),

  // Lists
  ul: ({ children }) => (
    <ul className="list-disc list-inside text-gray-600 mb-3 space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside text-gray-600 mb-3 space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">
      {children}
    </li>
  ),

  // Emphasis
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">
      {children}
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic text-gray-700">
      {children}
    </em>
  ),

  // Code
  code: ({ inline, children }) => {
    if (inline) {
      return (
        <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      );
    }
    return (
      <code className="block bg-gray-100 text-gray-800 p-3 rounded-lg text-sm font-mono whitespace-pre-wrap overflow-x-auto mb-3">
        {children}
      </code>
    );
  },

  // Code blocks
  pre: ({ children }) => (
    <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg text-sm font-mono whitespace-pre-wrap overflow-x-auto mb-4">
      {children}
    </pre>
  ),

  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-blue-200 pl-4 py-2 my-4 bg-blue-50 text-gray-700 italic">
      {children}
    </blockquote>
  ),

  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full border border-gray-200 rounded-lg">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-50">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-gray-200">
      {children}
    </tbody>
  ),
  tr: ({ children }) => (
    <tr>
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2 text-sm text-gray-600">
      {children}
    </td>
  ),

  // Horizontal rule
  hr: () => (
    <hr className="border-gray-200 my-6" />
  ),

  // Strikethrough (from GFM)
  del: ({ children }) => (
    <del className="line-through text-gray-500">
      {children}
    </del>
  ),
};

/**
 * Custom rehype sanitize schema for security
 * Allows safe HTML elements while preventing XSS
 */
const sanitizeSchema = {
  allowedTags: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'span', 'div',
    'strong', 'b', 'em', 'i', 'u', 'del', 's',
    'a', 'ul', 'ol', 'li',
    'code', 'pre', 'blockquote',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'hr'
  ],
  allowedAttributes: {
    'a': ['href', 'target', 'rel'],
    '*': ['className', 'class']
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowProtocolRelative: false
};

/**
 * MarkdownRenderer Component
 * @param {Object} props - Component props
 * @param {string} props.content - Markdown content to render
 * @param {string} props.className - Additional CSS classes for the container
 * @param {boolean} props.allowHtml - Whether to allow HTML in markdown (default: false)
 * @returns {JSX.Element} - Rendered markdown content
 */
const MarkdownRenderer = ({ 
  content, 
  className = '', 
  allowHtml = false 
}) => {
  // Temporarily use SimpleTextRenderer to fix build issues
  // TODO: Re-enable full markdown support once dependencies are resolved
  return <SimpleTextRenderer content={content} className={className} />;
  
  // Handle empty or invalid content
  // if (!content || typeof content !== 'string') {
  //   return <div className={className}></div>;
  // }

  // return (
  //   <div className={`markdown-content ${className}`}>
  //     <ReactMarkdown
  //       remarkPlugins={[remarkGfm]}
  //       rehypePlugins={[
  //         [rehypeSanitize, sanitizeSchema]
  //       ]}
  //       components={markdownComponents}
  //       allowedElements={allowHtml ? undefined : Object.keys(markdownComponents)}
  //       skipHtml={!allowHtml}
  //     >
  //       {content}
  //     </ReactMarkdown>
  //   </div>
  // );
};

/**
 * Simple text renderer that falls back to basic URL rendering
 * Useful for backward compatibility when markdown dependencies aren't available
 */
export const SimpleTextRenderer = ({ content, className = '' }) => {
  if (!content || typeof content !== 'string') {
    return <div className={className}></div>;
  }

  // Basic URL detection and replacement
  const urlRegex = /(https?:\/\/(?:[-\w.])+(?:\:[0-9]+)?(?:\/(?:[\w\/_.])*)?(?:\?(?:[\w&=%.])*)?(?:\#(?:[\w.])*)?)/gi;
  
  const parts = content.split(urlRegex);
  const elements = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      elements.push(
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline hover:no-underline transition-colors duration-200"
        >
          {part}
        </a>
      );
    } else {
      // Preserve line breaks
      const textWithBreaks = part.split('\n').map((line, lineIndex, array) => (
        <React.Fragment key={`${i}-${lineIndex}`}>
          {line}
          {lineIndex < array.length - 1 && <br />}
        </React.Fragment>
      ));
      elements.push(...textWithBreaks);
    }
  }

  return (
    <div className={`${className} whitespace-pre-line`}>
      {elements}
    </div>
  );
};

export default MarkdownRenderer;